import React from 'react';

import moment from 'moment';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { Table, TableBody, TableHead, TableRow, TableCell } from '@material-ui/core';

import { executeMergeEmployees } from './actions/execute-merge-employees';

import { UiButton } from '../ui/ui-button';
import { UiDialog } from '../ui/ui-dialog';

import { TableHeaderCell } from '../widgets/table-components/table-header-cell';

type InputProps = {
  employeeToKeep: Employee;
  employeeToRemove?: Employee;
  mergeResolution: Record<keyof EmployeeMergeResolutionType, string>;
  previousStep: () => void;
  close: () => void;
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { employeeToKeep, mergeResolution }: InputProps,
) => ({
  actions: {
    mergeEmployees: (employeeToRemoveId: number) =>
      dispatch(
        executeMergeEmployees(
          employeeToKeep.employeeId,
          employeeToRemoveId,
          employeeToKeep.orgId,
          mapMergeResolutionToApiExpectedType(mergeResolution),
        ),
      ),
  },
});

const mapMergeResolutionToApiExpectedType = (
  mergeResolution: Record<keyof EmployeeMergeResolutionType, string>,
): EmployeeMergeResolutionApiType => ({
  employeeFields: {
    startDate: mergeResolution.startDate
      ? moment.utc(mergeResolution.startDate, 'MM-DD-YYYY')
      : null,
    nickname: mergeResolution.nickname,
    jobTitle: mergeResolution.jobTitle,
  },
  personFields: {
    firstName: mergeResolution.firstName,
    lastName: mergeResolution.lastName,
    email: mergeResolution.email,
    phoneNumber: mergeResolution.phoneNumber,
    birthdate: mergeResolution.birthdate
      ? moment.utc(mergeResolution.birthdate, 'MM-DD-YYYY')
      : null,
  },
});

type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & DispatchProps;

export const EmployeeMergeConfirmation = connect(
  null,
  mapDispatchToProps,
)(({ employeeToRemove, mergeResolution, previousStep, close, actions }: Props) => {
  const handleSubmit = () => {
    if (employeeToRemove) {
      actions.mergeEmployees(employeeToRemove.employeeId);
      close();
    }
  };

  return (
    <>
      <UiDialog.Content>
        <div className="u-margin typo-label">Please confirm the following is correct</div>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableHeaderCell />
              <TableHeaderCell>Employee Details</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>
                <span className="typo-bold">First Name</span>
              </TableCell>
              <TableCell>{mergeResolution.firstName}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span className="typo-bold">Last Name</span>
              </TableCell>
              <TableCell>{mergeResolution.lastName}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span className="typo-bold">Nickname</span>
              </TableCell>
              <TableCell>{mergeResolution.nickname}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span className="typo-bold">Email</span>
              </TableCell>
              <TableCell>{mergeResolution.email}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span className="typo-bold">Phone Number</span>
              </TableCell>
              <TableCell>{mergeResolution.phoneNumber}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span className="typo-bold">Start Date</span>
              </TableCell>
              <TableCell>{mergeResolution.startDate}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span className="typo-bold">Job Title</span>
              </TableCell>
              <TableCell>{mergeResolution.jobTitle}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span className="typo-bold">Birthdate</span>
              </TableCell>
              <TableCell>{mergeResolution.birthdate}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </UiDialog.Content>
      <UiDialog.Actions>
        <UiButton variant="dismiss" onClick={close}>
          Cancel
        </UiButton>
        <div>
          <UiButton variant="secondary" className="u-margin-left" onClick={previousStep}>
            Back
          </UiButton>
          <UiButton variant="primary" className="u-margin-left" onClick={handleSubmit}>
            Submit
          </UiButton>
        </div>
      </UiDialog.Actions>
    </>
  );
});
